// src/hooks/usePagination.js
import { useState, useCallback, useMemo } from 'react';

/**
 * Hook for managing pagination state
 * Works with DataTable component's pagination prop pattern
 *
 * @param {Object} options - Configuration options
 * @param {number} options.initialPage - Starting page (default: 1)
 * @param {number} options.initialPageSize - Items per page (default: 20)
 * @param {number} options.totalItems - Total number of items (default: 0)
 * @returns {Object} pagination state and controls
 */
export const usePagination = ({
    initialPage = 1,
    initialPageSize = 20,
    totalItems: externalTotalItems = 0
} = {}) => {
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [totalItems, setTotalItems] = useState(externalTotalItems);

    const totalPages = useMemo(() => {
        return Math.ceil(totalItems / pageSize) || 1;
    }, [totalItems, pageSize]);

    const offset = useMemo(() => {
        return (currentPage - 1) * pageSize;
    }, [currentPage, pageSize]);

    const goToPage = useCallback((page) => {
        const validPage = Math.max(1, Math.min(page, totalPages));
        setCurrentPage(validPage);
    }, [totalPages]);

    const nextPage = useCallback(() => {
        if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
        }
    }, [currentPage, totalPages]);

    const prevPage = useCallback(() => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    }, [currentPage]);

    const reset = useCallback(() => {
        setCurrentPage(initialPage);
    }, [initialPage]);

    const updatePageSize = useCallback((newSize) => {
        setPageSize(newSize);
        setCurrentPage(1); // Reset to first page when changing page size
    }, []);

    const updateTotalItems = useCallback((total) => {
        setTotalItems(total);
        // Adjust current page if it's now out of bounds
        const newTotalPages = Math.ceil(total / pageSize) || 1;
        if (currentPage > newTotalPages) {
            setCurrentPage(newTotalPages);
        }
    }, [currentPage, pageSize]);

    return {
        currentPage,
        pageSize,
        totalItems,
        totalPages,
        offset,
        // Actions
        goToPage,
        nextPage,
        prevPage,
        reset,
        setPageSize: updatePageSize,
        setTotalItems: updateTotalItems,
        // Convenience flags
        hasNextPage: currentPage < totalPages,
        hasPrevPage: currentPage > 1,
        isFirstPage: currentPage === 1,
        isLastPage: currentPage === totalPages,
        // For API calls
        paginationVars: {
            limit: pageSize,
            offset
        }
    };
};

export default usePagination;
